// Per-sprite geometry produced by tools/refit-sprites.py.
//
// The renderer used to infer placement from `texture.width` / `texture.height`,
// which is only correct if every source render happens to be framed identically
// — it never is. Tiles came out overlapping and birds floated above their tile.
// The pipeline measures each sprite once, offline, and records the two things
// the renderer actually needs: where the anchor point sits, and how wide the
// isometric diamond is inside the image.
//
// Metadata is optional at runtime. A sprite with no entry falls back to the old
// bounds-based guess, and a missing manifest leaves the game fully procedural —
// same guarantee as a missing sprite (AGENT.md §3).

export interface SpriteMeta {
  file: string;
  w: number;
  h: number;
  /** Anchor in image pixels: a tile's diamond apex, an actor's feet. */
  pivotX: number;
  pivotY: number;
  kind: 'tile' | 'actor';
  /** Tiles: the top face's span, which is what the grid cell must match. */
  diamondW?: number;
  diamondH?: number;
  /** Actors: pivot-to-crown height, so breeds scale to a common size. */
  standHeight?: number;
}

interface SpriteManifestFile {
  version: number;
  sprites: Record<string, SpriteMeta>;
}

let byUrl = new Map<string, SpriteMeta>();

/** Metadata for a loaded texture path, or null when the sprite is unmeasured. */
export function metaFor(url: string): SpriteMeta | null {
  return byUrl.get(url) ?? null;
}

export function isLoaded(): boolean {
  return byUrl.size > 0;
}

/**
 * Fetches sprites.json. Never throws: the caller boots the game either way, so
 * a 404 here degrades the art rather than the session.
 */
export async function loadSpriteMeta(root: string): Promise<number> {
  try {
    const response = await fetch(`${root}/sprites.json`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const parsed = (await response.json()) as SpriteManifestFile;
    byUrl = new Map(
      Object.values(parsed.sprites).map((meta) => [`${root}/${meta.file}`, meta]),
    );
    return byUrl.size;
  } catch (err) {
    console.warn('[assets] sprite metadata unavailable, using bounds fallback', err);
    return 0;
  }
}

/** Test seam — lets a unit test install metadata without a network fetch. */
export function setSpriteMeta(entries: Record<string, SpriteMeta>): void {
  byUrl = new Map(Object.entries(entries));
}
